/**
 * Penetapan COA — Verifikasi 2 (Tahap 2 verifikasi dua tahap, 2026-10-13).
 *
 * Antrean terpisah untuk penetapan akun COA atas realisasi petty cash yang SUDAH
 * lolos Verifikasi 1. Pemeriksa HANYA: memilih akun, memeriksa kesesuaian
 * klasifikasi, mengonfirmasi, dan memperbaiki klasifikasi (dengan alasan + audit).
 * Bukti & hasil V1 ditampilkan READ-ONLY. Temuan di luar COA dicatat dan dirujuk
 * ke alur koreksi Verifikasi 1 — tidak ada perubahan nilai finansial dari sini.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import SearchableSelect from "@/components/SearchableSelect";
import DateField from "@/components/DateField";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatIDR, fmtDate } from "@/lib/constants";

const STATUS_OPTS = [
  { value: "awaiting_coa", label: "Belum Ditetapkan" },
  { value: "coa_review_needed", label: "Perlu Ditinjau Ulang" },
  { value: "coa_set", label: "Sudah Ditetapkan" },
];
const STATUS_BADGE = {
  awaiting_coa: "bg-amber-50 border-amber-300 text-amber-800",
  coa_review_needed: "bg-rose-50 border-rose-300 text-rose-700",
  coa_set: "bg-emerald-50 border-emerald-300 text-emerald-700",
};
const STATUS_LABEL = Object.fromEntries(STATUS_OPTS.map((o) => [o.value, o.label]));

export default function PenetapanCoaPage() {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const allowed = canPerm(user, "petty_cash", "verify_coa") || canPerm(user, "petty_cash", "verify_actual");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coaList, setCoaList] = useState([]);
  const [fStatus, setFStatus] = useState("awaiting_coa");
  const [fUser, setFUser] = useState("");
  const [fProject, setFProject] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [coaPick, setCoaPick] = useState({});      // `${pc_id}:${item_id}` -> coa_id
  const [reason, setReason] = useState({});        // key -> reason text
  const [findingFor, setFindingFor] = useState(null);
  const [findingNote, setFindingNote] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/petty-cash/coa-queue", { params: { status: "all" } });
      setRows(data?.rows || []);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
      setRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!allowed) return;
    load();
    api.get("/finance/chart-of-accounts")
      .then((r) => setCoaList(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCoaList([]));
  }, [allowed, load]);

  // Hanya akun detail aktif (bukan induk) yang boleh dipilih — backend memvalidasi ulang.
  const coaOptions = useMemo(
    () => coaList.filter((c) => !c.is_parent && c.is_active !== false).map((c) => ({ value: c.id, label: `${c.code} — ${c.name}` })),
    [coaList]);

  const userOptions = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.user_id) m.set(r.user_id, r.user_name || r.user_id); });
    return [...m.entries()].map(([value, label]) => ({ value, label }));
  }, [rows]);
  const projectOptions = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.project_id) m.set(r.project_id, r.project_name || r.project_id); });
    return [...m.entries()].map(([value, label]) => ({ value, label }));
  }, [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (fStatus !== "all" && r.coa_status !== fStatus) return false;
    if (fUser && r.user_id !== fUser) return false;
    if (fProject && r.project_id !== fProject) return false;
    const d = String(r.actual_transaction_date || "").slice(0, 10);
    if (fFrom && d && d < fFrom) return false;
    if (fTo && d && d > fTo) return false;
    return true;
  }), [rows, fStatus, fUser, fProject, fFrom, fTo]);

  const totals = useMemo(() => ({
    awaiting: rows.filter((r) => r.coa_status === "awaiting_coa").length,
    review: rows.filter((r) => r.coa_status === "coa_review_needed").length,
    done: rows.filter((r) => r.coa_status === "coa_set").length,
    amount: filtered.reduce((s, r) => s + (Number(r.actual_amount) || 0), 0),
  }), [rows, filtered]);

  const saveCoa = async (row) => {
    const key = `${row.pc_id}:${row.item_id}`;
    const coaId = coaPick[key];
    if (!coaId) { toast.error("Pilih akun COA dulu"); return; }
    const changing = !!row.coa_id && row.coa_id !== coaId;
    const rsn = (reason[key] || "").trim();
    if (changing && !rsn) { toast.error("Alasan wajib diisi saat mengubah klasifikasi yang sudah ada"); return; }
    setBusy(key);
    try {
      await api.patch(`/petty-cash/${row.pc_id}/items/${row.item_id}/coa`, { coa_id: coaId, reason: rsn || null });
      toast.success(changing ? "Klasifikasi COA diperbaiki (tercatat di audit)" : "COA dikonfirmasi");
      setCoaPick((p) => ({ ...p, [key]: "" }));
      setReason((p) => ({ ...p, [key]: "" }));
      await load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const sendFinding = async (row) => {
    const key = `${row.pc_id}:${row.item_id}`;
    if (!findingNote.trim()) { toast.error("Catatan temuan wajib diisi"); return; }
    setBusy(key);
    try {
      await api.post(`/petty-cash/${row.pc_id}/items/${row.item_id}/coa-finding`, { note: findingNote.trim() });
      toast.success("Temuan dicatat & dirujuk ke alur koreksi Verifikasi 1");
      setFindingFor(null); setFindingNote("");
      await load();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  if (!allowed) {
    return (
      <div className="p-8 text-center text-sm text-slate-500" data-testid="coa-queue-forbidden">
        Anda tidak memiliki akses Verifikasi 2 (penetapan COA). Hubungi admin — Access Control → Weekly Petty Cash → Verifikasi 2.
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="penetapan-coa-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#0B1D3A]">Penetapan COA — Verifikasi 2</h1>
          <p className="text-xs text-slate-500 mt-1 max-w-3xl">
            Antrean penetapan akun COA untuk realisasi petty cash yang sudah lolos <b>Verifikasi 1</b>.
            Tahap ini hanya mengklasifikasikan akun — nominal, tanggal, proyek, sumber dana, dan bukti
            <b> tidak dapat diubah</b> dari sini. Menemukan masalah di luar COA? Catat temuan — koreksi
            finansial mengikuti alur Verifikasi 1.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="coa-queue-refresh" className="rounded-none">
          <RefreshCw size={13} className="mr-1" /> Muat Ulang
        </Button>
      </div>

      {/* Filter bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end border border-slate-200 bg-slate-50 p-3">
        <div>
          <div className="overline text-slate-500 text-[9px] mb-0.5">Status</div>
          <SearchableSelect testid="coa-filter-status" value={fStatus} onChange={setFStatus}
            options={[{ value: "all", label: "Semua Status" }, ...STATUS_OPTS]} placeholder="Status" />
        </div>
        <div>
          <div className="overline text-slate-500 text-[9px] mb-0.5">Staf</div>
          <SearchableSelect testid="coa-filter-user" value={fUser} onChange={setFUser}
            options={[{ value: "", label: "Semua Staf" }, ...userOptions]} placeholder="Staf"
            searchPlaceholder="Ketik nama staf…" />
        </div>
        <div>
          <div className="overline text-slate-500 text-[9px] mb-0.5">Proyek</div>
          <SearchableSelect testid="coa-filter-project" value={fProject} onChange={setFProject}
            options={[{ value: "", label: "Semua Proyek" }, ...projectOptions]} placeholder="Proyek"
            searchPlaceholder="Ketik nama proyek…" />
        </div>
        <div>
          <div className="overline text-slate-500 text-[9px] mb-0.5">Dari Tanggal</div>
          <DateField testid="coa-filter-from" value={fFrom} onChange={setFFrom} />
        </div>
        <div>
          <div className="overline text-slate-500 text-[9px] mb-0.5">Sampai Tanggal</div>
          <DateField testid="coa-filter-to" value={fTo} onChange={setFTo} />
        </div>
      </div>

      {/* Ringkasan */}
      <div className="flex gap-4 text-[11px] flex-wrap" data-testid="coa-queue-summary">
        <span className="text-amber-700">Belum ditetapkan: <b>{totals.awaiting}</b></span>
        <span className="text-rose-700">Perlu ditinjau ulang: <b>{totals.review}</b></span>
        <span className="text-emerald-700">Sudah ditetapkan: <b>{totals.done}</b></span>
        <span className="text-slate-500 ml-auto">Total nominal (filter aktif): <b className="tabular-nums">{formatIDR(totals.amount)}</b></span>
      </div>

      {/* Tabel antrean */}
      <div className="border border-slate-200 overflow-auto" data-testid="coa-queue-table">
        <table className="w-full text-xs min-w-[1400px]">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              {["Staf / Minggu", "Proyek", "Item & Bukti", "Nominal", "Hasil Verifikasi 1", "Status COA", "Penetapan COA (Verifikasi 2)", ""].map((h) => (
                <th key={h} className="text-left px-2 py-1.5 border-b border-slate-200 sticky top-0 bg-slate-100">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center p-6 text-slate-400">Memuat…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center p-6 text-slate-500">Tidak ada item pada filter ini.</td></tr>
            )}
            {!loading && filtered.map((r) => {
              const key = `${r.pc_id}:${r.item_id}`;
              const picked = coaPick[key] || "";
              const changing = !!r.coa_id && picked && picked !== r.coa_id;
              return (
                <tr key={key} className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`coa-queue-row-${r.item_id}`}>
                  <td className="px-2 py-2">
                    <div className="font-medium text-[#0B1D3A]">{r.user_name}</div>
                    <div className="text-[10px] text-slate-500">{r.week_label}</div>
                  </td>
                  <td className="px-2 py-2">{r.project_name || <span className="italic text-slate-400">Office</span>}</td>
                  <td className="px-2 py-2 max-w-[280px]">
                    <div className="font-medium">{r.item_name}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 space-y-0.5">
                      <div>Tgl: {r.actual_transaction_date ? fmtDate(r.actual_transaction_date) : "—"} · Voucher: {r.voucher_no || "—"} ({r.voucher_date ? fmtDate(r.voucher_date) : "—"})</div>
                      {r.supplier && <div>Vendor: {r.supplier}</div>}
                      {r.actual_note && <div className="italic">"{r.actual_note}"</div>}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums font-semibold">{formatIDR(r.actual_amount)}</td>
                  <td className="px-2 py-2 text-[10px] text-slate-600">
                    <div className="inline-flex items-center gap-1 text-emerald-700 font-semibold uppercase text-[9px]"><ShieldCheck size={10} /> Lolos V1</div>
                    <div>oleh {r.item_verified_by_name || "—"}</div>
                    <div>{r.item_verified_at ? String(r.item_verified_at).slice(0, 16).replace("T", " ") : ""}</div>
                    {r.v1_decision?.realization_version && <div className="text-slate-400">versi realisasi v{r.v1_decision.realization_version}</div>}
                  </td>
                  <td className="px-2 py-2">
                    <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border ${STATUS_BADGE[r.coa_status]}`}
                      data-testid={`coa-queue-status-${r.item_id}`}>
                      {STATUS_LABEL[r.coa_status]}
                    </span>
                    {r.coa_code && <div className="text-[10px] text-slate-600 mt-0.5 font-mono">{r.coa_code} — {r.coa_name}</div>}
                    {r.coa_set_by_name && <div className="text-[9px] text-slate-400">oleh {r.coa_set_by_name}</div>}
                    {(r.coa_history || []).length > 0 && (
                      <div className="text-[9px] text-slate-400" title={r.coa_history.map((h) => `${h.old_coa_code || "—"}→${h.new_coa_code} (${h.reason || "tanpa alasan"})`).join("; ")}>
                        riwayat: {r.coa_history.length}x perubahan
                      </div>
                    )}
                    {r.coa_finding_note && (
                      <div className="text-[9px] text-amber-700 mt-0.5" title={r.coa_finding_note}>⚠ ada temuan tercatat</div>
                    )}
                  </td>
                  <td className="px-2 py-2 min-w-[240px]">
                    <SearchableSelect
                      testid={`coa-queue-select-${r.item_id}`}
                      value={picked || r.coa_id || ""}
                      onChange={(v) => setCoaPick((p) => ({ ...p, [key]: v }))}
                      options={coaOptions}
                      placeholder="Pilih akun COA…"
                      searchPlaceholder="Ketik kode / nama akun…"
                      emptyText="Akun tidak ditemukan"
                    />
                    {changing && (
                      <Input
                        data-testid={`coa-queue-reason-${r.item_id}`}
                        value={reason[key] || ""}
                        onChange={(ev) => setReason((p) => ({ ...p, [key]: ev.target.value }))}
                        placeholder="Alasan ubah klasifikasi *"
                        className="rounded-none h-7 text-xs mt-1"
                      />
                    )}
                    <button
                      type="button"
                      data-testid={`coa-queue-save-${r.item_id}`}
                      disabled={busy === key || !picked || picked === r.coa_id}
                      onClick={() => saveCoa(r)}
                      className="mt-1 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white bg-[#0B1D3A] hover:bg-[#16294d] disabled:opacity-40"
                    >
                      {changing ? "Perbaiki Klasifikasi" : "Konfirmasi COA"}
                    </button>
                  </td>
                  <td className="px-2 py-2">
                    {findingFor === key ? (
                      <div className="space-y-1 min-w-[200px]">
                        <Input
                          data-testid={`coa-finding-note-${r.item_id}`}
                          value={findingNote}
                          onChange={(ev) => setFindingNote(ev.target.value)}
                          placeholder="Temuan di luar COA *"
                          className="rounded-none h-7 text-xs"
                          autoFocus
                        />
                        <div className="flex gap-1">
                          <button type="button" data-testid={`coa-finding-send-${r.item_id}`}
                            onClick={() => sendFinding(r)} disabled={busy === key}
                            className="px-2 py-0.5 text-[10px] font-semibold uppercase text-white bg-amber-600 hover:bg-amber-700">Catat & Rujuk ke V1</button>
                          <button type="button" onClick={() => { setFindingFor(null); setFindingNote(""); }}
                            className="px-2 py-0.5 text-[10px] uppercase text-slate-600 border border-slate-300">✕</button>
                        </div>
                      </div>
                    ) : (
                      <button type="button" data-testid={`coa-finding-open-${r.item_id}`}
                        onClick={() => { setFindingFor(key); setFindingNote(""); }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 border border-amber-300 hover:bg-amber-50">
                        <AlertTriangle size={10} /> Temuan
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-slate-400 italic">
        Data operasional website — bukan pembukuan resmi eksternal. Penetapan/perubahan COA memindahkan klasifikasi, bukan membuat transaksi biaya baru; saldo kas & pertanggungjawaban tidak berubah.
      </div>
    </div>
  );
}
